import type { RelayToDevice } from '@relyo/protocol';
import { Annotations } from './annotations';
import { Connection, deviceSocketUrl, type HelloPayload } from './connection';
import { CODE_TTL_MS, PROTO_VERSION, SDK_VERSION } from './constants';
import {
  RemoteControl,
  type ControlCommand,
  type ControlReply,
  type ControlState,
} from './control';
import { de } from './i18n';
import {
  STATUS_POSITIONS,
  type ConnectMode,
  type RelyoOptions,
  type RelyoUser,
  type ResolvedOptions,
  type StatusPosition,
} from './options';
import { Recorder } from './recorder';
import { buildRedactSelector, isValidSelector, type RedactionConfig } from './redaction';
import {
  clearSession,
  getDeviceId,
  getTabId,
  loadSession,
  saveSession,
  type StoredSession,
} from './storage';
import { RelyoUi } from './ui';
import { watchNavigation } from './navigation';

/** Ohne Code nach dieser Zeit zeigt das Code-Modal einen Verbindungsfehler (Versuche laufen weiter). */
const CODE_WAIT_MS = 10_000;
/** Antwortet der Relay nicht auf `session.resume`, wird lokal aufgeräumt. */
const RESUME_WAIT_MS = 15_000;

const FATAL_ERRORS = new Set(['unknown_project', 'origin_denied', 'proto_unsupported', 'blocked']);

type Phase =
  | { kind: 'idle' }
  | { kind: 'code'; requested: boolean; expired: boolean }
  | { kind: 'consent'; sessionId: string; agentName: string }
  | { kind: 'starting'; sessionId: string; agentName: string }
  | {
      kind: 'active';
      sessionId: string;
      agentName: string;
      resumeToken: string;
      /** `true`, solange nach einem Verbindungsabbruch auf `session.started` gewartet wird. */
      resuming: boolean;
      resumeMsgId: string | null;
    };

export type PhaseKind = Phase['kind'];

export interface RelyoInstance {
  /** Fordert einen 6-stelligen Code an und zeigt ihn im Modal (UC1). */
  showCode(): void;
  /** Beendet eine laufende Session bzw. bricht Code/Consent ab. */
  end(): void;
  /** Setzt bzw. löscht (Logout: `null`) den angemeldeten Nutzer. */
  identify(user: RelyoUser | null): void;
  /**
   * Baut das SDK ab (rrweb, UI, Socket, Listener). Eine laufende Session wird nicht aktiv
   * beendet, sondern kann von einem erneuten `init` im selben Tab fortgesetzt werden – zum
   * expliziten Beenden `end()` aufrufen. Die Instanz ist danach unbrauchbar.
   */
  destroy(): void;
}

function normalizeUser(user: RelyoUser | null | undefined): RelyoUser | null {
  if (!user || typeof user.id !== 'string' || user.id.length === 0) return null;
  const out: RelyoUser = { id: user.id.slice(0, 200) };
  if (typeof user.name === 'string') out.name = user.name.slice(0, 200);
  return out;
}

export function resolveOptions(o: RelyoOptions): ResolvedOptions {
  if (!o || typeof o.relayUrl !== 'string' || o.relayUrl.trim() === '') {
    throw new Error('Relyo.init: relayUrl fehlt');
  }
  if (o.projectKey !== undefined && o.projectKey !== null && typeof o.projectKey !== 'string') {
    throw new Error('Relyo.init: projectKey muss ein String sein');
  }
  // Leer oder fehlend: offener Dienst (der Relay ordnet die Seite ihrem Origin zu).
  const projectKey = o.projectKey?.trim() || null;
  const connect: ConnectMode =
    o.connect === 'eager' || o.connect === 'lazy' || o.connect === 'auto' ? o.connect : 'auto';
  let maskAllInputs = o.maskAllInputs === true;
  let redactSelector = buildRedactSelector(o.redactSelector);
  if (!isValidSelector(redactSelector)) {
    // Fail closed: lieber alles maskieren als sensible Felder durchlassen.
    console.warn('[relyo] Ungültiger redactSelector – es werden alle Eingaben maskiert.');
    redactSelector = buildRedactSelector(null);
    maskAllInputs = true;
  }
  return {
    relayUrl: o.relayUrl.trim(),
    projectKey,
    user: normalizeUser(o.user),
    redactSelector,
    maskAllInputs,
    connect,
    locale: 'de',
    consentText: typeof o.consentText === 'string' && o.consentText ? o.consentText : null,
    cspNonce: typeof o.cspNonce === 'string' && o.cspNonce ? o.cspNonce : null,
    appVersion: typeof o.appVersion === 'string' ? o.appVersion.slice(0, 64) : '',
    statusPosition: STATUS_POSITIONS.includes(o.statusPosition as StatusPosition)
      ? (o.statusPosition as StatusPosition)
      : 'bottom',
  };
}

export class RelyoClient implements RelyoInstance {
  private readonly opts: ResolvedOptions;
  private readonly conn: Connection;
  private readonly ui: RelyoUi;
  private readonly recorder: Recorder;
  private readonly annotations: Annotations;
  private readonly control: RemoteControl;
  // Erst beim ersten Verbindungsaufbau angelegt: Besucher, die nie „Support“ wählen, bekommen
  // nichts in localStorage/sessionStorage geschrieben (§ 25 TDDDG).
  private deviceId: string | null = null;
  private tabId: string | null = null;
  private phase: Phase = { kind: 'idle' };
  private clockOffset = 0;
  private destroyed = false;
  private lastPath: string;
  private codeWaitTimer: ReturnType<typeof setTimeout> | null = null;
  private resumeWaitTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly cleanups: (() => void)[] = [];

  constructor(options: RelyoOptions) {
    this.opts = resolveOptions(options);
    this.lastPath = location.pathname;

    const redaction: RedactionConfig = {
      redactSelector: this.opts.redactSelector,
      maskAllInputs: this.opts.maskAllInputs,
    };
    this.recorder = new Recorder(redaction, (packed, full) => this.sendEvents(packed, full));
    this.ui = new RelyoUi(
      {
        onCodeCancel: () => this.cancelCode(),
        onCodeRetry: () => this.retryCode(),
        onCodeExpired: () => this.codeExpired(),
        onConsent: (granted) => this.answerConsent(granted),
        onEnd: () => this.end(),
        onControlAnswer: (granted) => this.control.answer(granted),
        onControlStop: () => this.control.stop(),
      },
      this.opts.cspNonce,
      this.opts.statusPosition,
    );
    this.annotations = new Annotations(() => this.ui.overlay());
    this.control = new RemoteControl(
      {
        reply: (msg) => this.sendControlReply(msg),
        getNode: (id) => this.recorder.getNode(id),
        isOwnEvent: (ev) => this.ui.isOwnEvent(ev),
        isOwnNode: (n) => this.ui.isOwnNode(n),
        render: (state, agentName) => this.renderControl(state, agentName),
      },
      redaction,
    );
    this.conn = new Connection(deviceSocketUrl(this.opts.relayUrl, this.opts.projectKey), {
      hello: () => this.hello(),
      onReady: () => this.onReady(),
      onMessage: (m) => this.onMessage(m),
      onClose: () => this.onClose(),
    });

    this.installListeners();

    const stored = loadSession();
    if (stored) {
      // Vollseiten-Navigation/Reload während einer Session: fortsetzen (Spec Kap. 5 Punkt 8).
      this.phase = {
        kind: 'active',
        sessionId: stored.sessionId,
        agentName: stored.agentName,
        resumeToken: stored.resumeToken,
        resuming: true,
        resumeMsgId: null,
      };
      this.conn.open();
    } else if (this.keepIdleConnection()) {
      this.conn.open();
    }
  }

  /** Aktuelle Phase (für Tests und Debugging). */
  get state(): PhaseKind {
    return this.phase.kind;
  }

  get isDestroyed(): boolean {
    return this.destroyed;
  }

  /** Socket gewünscht/offen (für Tests). */
  get connected(): boolean {
    return this.conn.isWanted;
  }

  /** Zustand der Fernsteuerung (für Tests). */
  get controlState(): ControlState {
    return this.control.state;
  }

  /** Anzahl sichtbarer Zeichnungen (für Tests). */
  get drawingCount(): number {
    return this.annotations.drawingCount;
  }

  // ------------------------------------------------------------- Public API

  showCode(): void {
    if (this.destroyed || this.phase.kind !== 'idle') return;
    this.phase = { kind: 'code', requested: false, expired: false };
    this.ui.showCodeLoading();
    this.startCodeWait();
    this.conn.open();
    if (this.conn.isReady) this.requestCode();
  }

  end(): void {
    if (this.destroyed) return;
    const p = this.phase;
    switch (p.kind) {
      case 'idle':
        return;
      case 'code':
        this.cancelCode();
        return;
      case 'consent':
        this.answerConsent(false);
        return;
      case 'starting':
      case 'active':
        this.conn.send('session.end', { sessionId: p.sessionId, reason: 'user_ended' });
        this.cleanupSession();
        return;
    }
  }

  identify(user: RelyoUser | null): void {
    if (this.destroyed) return;
    this.opts.user = normalizeUser(user);
    if (this.conn.isReady) this.conn.send('device.identify', { user: this.opts.user });
    if (this.keepIdleConnection()) this.conn.open();
    else this.maybeDisconnect();
  }

  destroy(): void {
    if (this.destroyed) return;
    const p = this.phase;
    if (p.kind === 'code') this.conn.send('session.codeCancel', {});
    if (p.kind === 'consent') {
      this.conn.send('session.consent', { sessionId: p.sessionId, granted: false });
    }
    // Eine laufende Session wird bewusst NICHT beendet und der Resume-Eintrag bleibt erhalten:
    // Ein erneutes `init` im selben Tab (z. B. React StrictMode, Remount) setzt sie fort. Ohne
    // erneutes `init` beendet der Relay sie nach 30 s (`publisher_lost`). rrweb stoppt sofort.
    this.destroyed = true;
    this.phase = { kind: 'idle' };
    this.clearTimers();
    this.control.reset();
    this.annotations.destroy();
    this.recorder.stop();
    this.ui.destroy();
    this.conn.close();
    for (const c of this.cleanups.splice(0)) c();
  }

  // ------------------------------------------------------------ Verbindung

  private hello(): HelloPayload {
    this.deviceId ??= getDeviceId();
    this.tabId ??= getTabId();
    const p: HelloPayload = {
      deviceId: this.deviceId,
      tabId: this.tabId,
      platform: 'web',
      appVersion: this.opts.appVersion,
      sdkVersion: SDK_VERSION,
      proto: PROTO_VERSION,
      url: location.pathname,
    };
    if (this.opts.projectKey) p.projectKey = this.opts.projectKey;
    if (this.opts.user) p.user = this.opts.user;
    return p;
  }

  private keepIdleConnection(): boolean {
    return this.opts.connect === 'eager' || (this.opts.connect === 'auto' && !!this.opts.user);
  }

  private maybeDisconnect(): void {
    if (this.phase.kind === 'idle' && !this.keepIdleConnection()) this.conn.close();
  }

  private onReady(): void {
    this.lastPath = location.pathname;
    const p = this.phase;
    if (p.kind === 'code' && !p.expired) {
      // Erstverbindung oder Reconnect: ein Code gehört zur Verbindung, daher neu anfordern.
      this.ui.showCodeLoading();
      this.requestCode();
    } else if (p.kind === 'starting') {
      // Ohne Resume-Token lässt sich die Session nicht fortsetzen.
      this.cleanupSession();
    } else if (p.kind === 'active') {
      p.resuming = true;
      p.resumeMsgId = this.conn.send('session.resume', {
        sessionId: p.sessionId,
        resumeToken: p.resumeToken,
      });
      this.startResumeWait();
    }
  }

  private onClose(): void {
    const p = this.phase;
    if (p.kind === 'code') {
      p.requested = false;
    } else if (p.kind === 'active') {
      // Der Relay beendet die Steuerung bei Verbindungsverlust selbst (ohne Nachricht).
      this.control.reset();
      this.annotations.clear();
      this.recorder.stop();
      p.resuming = true;
      this.ui.showStatus(p.agentName, 'reconnecting');
    }
  }

  private onMessage(m: RelayToDevice): void {
    if (this.destroyed) return;
    const p = this.phase;
    switch (m.t) {
      case 'device.ack':
        if (typeof m.p.serverTime === 'number') this.clockOffset = m.p.serverTime - Date.now();
        return;
      case 'session.code':
        if (p.kind === 'code' && !p.expired) {
          this.stopCodeWait();
          // Netzlatenz im Offset kann die Restzeit über die Code-Laufzeit heben („10:01“).
          const local = Math.min(m.p.expiresAt - this.clockOffset, Date.now() + CODE_TTL_MS);
          this.ui.showCode(m.p.code, local);
        }
        return;
      case 'session.request':
        if (p.kind === 'idle' || p.kind === 'code') {
          this.stopCodeWait();
          this.phase = { kind: 'consent', sessionId: m.p.sessionId, agentName: m.p.agentName };
          this.ui.showConsent({
            agentName: m.p.agentName,
            recording: m.p.recording === true,
            consentText: this.opts.consentText,
          });
        } else if (!('sessionId' in p) || p.sessionId !== m.p.sessionId) {
          // Es läuft bereits eine Session in diesem Tab: weitere Anfragen ablehnen.
          this.conn.send('session.consent', { sessionId: m.p.sessionId, granted: false });
        }
        return;
      case 'session.started':
        if ((p.kind === 'starting' || p.kind === 'active') && p.sessionId === m.p.sessionId) {
          this.startSession({
            sessionId: p.sessionId,
            resumeToken: m.p.resumeToken,
            agentName: m.p.agentName || p.agentName,
          });
        }
        return;
      case 'session.snapshotRequest':
        if (p.kind === 'active' && !p.resuming && p.sessionId === m.p.sessionId) {
          this.recorder.takeFullSnapshot();
        }
        return;
      case 'session.ended':
        if ('sessionId' in p && p.sessionId === m.p.sessionId) this.cleanupSession();
        return;
      case 'annot.pointer':
        if (this.isLive(m.p.sessionId)) this.annotations.pointer(m.p.x, m.p.y, m.p.label);
        return;
      case 'annot.draw':
        if (this.isLive(m.p.sessionId)) this.annotations.draw(m.p.shape, m.p.points, m.p.color);
        return;
      case 'annot.clear':
        if (this.isLive(m.p.sessionId)) this.annotations.clear();
        return;
      case 'control.request':
        if (this.isLive(m.p.sessionId)) {
          this.control.request(m.p.agentName || (p.kind === 'active' ? p.agentName : ''));
        } else if ('sessionId' in p && p.sessionId === m.p.sessionId) {
          this.conn.send('control.denied', { sessionId: m.p.sessionId });
        }
        return;
      case 'control.end':
        if ('sessionId' in p && p.sessionId === m.p.sessionId) this.control.reset();
        return;
      case 'control.click':
      case 'control.input':
      case 'control.scroll':
      case 'control.key':
      case 'control.tap':
      case 'control.drag':
      case 'control.text':
        if ('sessionId' in p && p.sessionId === m.p.sessionId) this.runControl(m);
        return;
      case 'error':
        this.onError(m.p.code, m.p.ref);
        return;
      default:
        return;
    }
  }

  /** Session läuft und streamt (nicht im Resume). */
  private isLive(sessionId: string): boolean {
    const p = this.phase;
    return p.kind === 'active' && !p.resuming && p.sessionId === sessionId;
  }

  // ------------------------------------------------------- Remote Control

  private runControl(cmd: ControlCommand): void {
    // Ohne laufende Session (z. B. im Resume) ist die Steuerung lokal immer `none`.
    if (!this.isLive(cmd.p.sessionId)) this.control.reset();
    this.control.execute(cmd);
  }

  private sendControlReply(msg: ControlReply): void {
    const p = this.phase;
    if (!('sessionId' in p)) return;
    const sessionId = p.sessionId;
    switch (msg.t) {
      case 'control.granted':
      case 'control.denied':
        this.conn.send(msg.t, { sessionId });
        return;
      case 'control.revoked':
        this.conn.send('control.revoked', { sessionId, reason: msg.reason });
        return;
      case 'control.rejected':
        this.conn.send('control.rejected', { sessionId, cmdId: msg.cmdId, reason: msg.reason });
        return;
    }
  }

  private renderControl(state: ControlState, agentName: string): void {
    if (state === 'requested') this.ui.showControlRequest(agentName);
    else this.ui.closeControlRequest();
    this.ui.setControlActive(state === 'granted' ? agentName : null);
  }

  private onError(code: string, ref: string | undefined): void {
    const p = this.phase;
    if (FATAL_ERRORS.has(code)) {
      console.warn(`[relyo] Relay lehnt die Verbindung ab: ${code}`);
      if (code === 'unknown_project' && !this.opts.projectKey) {
        console.warn('[relyo] Dieser Relay verlangt einen projectKey (kein offener Dienst).');
      }
      this.conn.close();
      if (p.kind === 'code') {
        this.stopCodeWait();
        this.ui.showCodeError(code === 'blocked' ? de.errorBlocked : de.errorConnect);
      } else if (p.kind !== 'idle') {
        this.cleanupSession();
      }
      return;
    }
    if (p.kind === 'code' && code === 'capacity') {
      this.stopCodeWait();
      this.ui.showCodeError(de.errorCapacity);
      return;
    }
    if (p.kind === 'active' && p.resuming && (ref === undefined || ref === p.resumeMsgId)) {
      // Resume abgelehnt (Session vorbei, Token falsch …).
      this.cleanupSession();
      return;
    }
    if (p.kind === 'code' && (ref === undefined || code === 'bad_state')) {
      this.stopCodeWait();
      this.ui.showCodeError(de.errorGeneric);
    }
  }

  private sendEvents(packed: string, full: boolean): void {
    const p = this.phase;
    if (p.kind !== 'active' || p.resuming) return;
    const payload: { sessionId: string; packed: string; full?: boolean } = {
      sessionId: p.sessionId,
      packed,
    };
    if (full) payload.full = true;
    this.conn.send('rr.events', payload);
  }

  // ---------------------------------------------------------- Code-Ablauf

  private requestCode(): void {
    const p = this.phase;
    if (p.kind !== 'code') return;
    p.requested = this.conn.send('session.codeRequest', {}) !== null;
  }

  private retryCode(): void {
    const p = this.phase;
    if (p.kind !== 'code') return;
    p.expired = false;
    p.requested = false;
    this.ui.showCodeLoading();
    this.startCodeWait();
    this.conn.open();
    if (this.conn.isReady) this.requestCode();
  }

  private codeExpired(): void {
    const p = this.phase;
    if (p.kind !== 'code') return;
    p.expired = true;
    this.ui.showCodeExpired();
  }

  private cancelCode(): void {
    if (this.phase.kind !== 'code') return;
    this.conn.send('session.codeCancel', {});
    this.stopCodeWait();
    this.phase = { kind: 'idle' };
    this.ui.closeModal();
    this.maybeDisconnect();
  }

  private startCodeWait(): void {
    this.stopCodeWait();
    this.codeWaitTimer = setTimeout(() => {
      this.codeWaitTimer = null;
      if (this.phase.kind === 'code') this.ui.showCodeError(de.errorConnect);
    }, CODE_WAIT_MS);
  }

  private stopCodeWait(): void {
    if (this.codeWaitTimer !== null) clearTimeout(this.codeWaitTimer);
    this.codeWaitTimer = null;
  }

  // ------------------------------------------------------- Consent/Session

  private answerConsent(granted: boolean): void {
    const p = this.phase;
    if (p.kind !== 'consent') return;
    const sent = this.conn.send('session.consent', { sessionId: p.sessionId, granted });
    this.ui.closeModal();
    if (granted && sent !== null) {
      // rrweb startet erst mit `session.started` (folgt auf die Freigabe).
      this.phase = { kind: 'starting', sessionId: p.sessionId, agentName: p.agentName };
    } else {
      this.phase = { kind: 'idle' };
      this.maybeDisconnect();
    }
  }

  private startSession(s: StoredSession): void {
    this.stopResumeWait();
    this.phase = {
      kind: 'active',
      sessionId: s.sessionId,
      agentName: s.agentName,
      resumeToken: s.resumeToken,
      resuming: false,
      resumeMsgId: null,
    };
    saveSession(s);
    this.ui.closeModal();
    this.ui.showStatus(s.agentName, 'live');
    // Neustart erzeugt einen frischen FullSnapshot (auch nach Resume).
    this.recorder.start();
  }

  private cleanupSession(): void {
    this.clearTimers();
    this.control.reset();
    this.annotations.destroy();
    this.recorder.stop();
    this.ui.closeModal();
    this.ui.removeOverlay();
    this.ui.hideStatus();
    clearSession();
    this.phase = { kind: 'idle' };
    this.maybeDisconnect();
  }

  private startResumeWait(): void {
    this.stopResumeWait();
    this.resumeWaitTimer = setTimeout(() => {
      this.resumeWaitTimer = null;
      const p = this.phase;
      if (p.kind === 'active' && p.resuming && this.conn.isReady) this.cleanupSession();
    }, RESUME_WAIT_MS);
  }

  private stopResumeWait(): void {
    if (this.resumeWaitTimer !== null) clearTimeout(this.resumeWaitTimer);
    this.resumeWaitTimer = null;
  }

  private clearTimers(): void {
    this.stopCodeWait();
    this.stopResumeWait();
  }

  // ------------------------------------------------------- Fokus/Navigation

  private installListeners(): void {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') this.sendFocus();
    };
    const onFocus = () => this.sendFocus();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    this.cleanups.push(() => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    });
    this.cleanups.push(
      watchNavigation((kind) => {
        // SPA-Navigation löscht Zeichnungen sofort (Vollseiten-Navigation baut alles ab).
        if (kind !== 'replace') this.annotations.clearDrawings();
        const path = location.pathname;
        if (path === this.lastPath) return;
        this.lastPath = path;
        this.sendFocus();
      }),
    );
  }

  private sendFocus(): void {
    if (this.conn.isReady) this.conn.send('device.focus', { url: location.pathname });
  }
}
